import { memo, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { flattenComponent, shadowColor } from '../../../utils/tellraw';
import type { ClickEvent, PreviewSpan } from '../../../utils/tellraw/types';
import { cn } from '../../../utils/cn';

/**
 * A tellraw as a player sees it in chat.
 *
 * It always draws on Minecraft's own dark chat backing, with the game's exact
 * palette, in both Argus themes. The light theme's `--mc-*` tokens are tuned
 * for reading `§` codes on white, which would misrepresent the message here:
 * the question this answers is "what will players see", not "what fits the
 * page". Colours are inline because they come from the message itself.
 */

interface TellrawPreviewProps {
  /** A JSON text component, compiled or imported. */
  component: unknown;
  /** One line, cut to fit: for table rows and library cards. */
  compact?: boolean;
  /** Shown in the chat box when the component has no text. */
  emptyLabel?: string;
  className?: string;
}

const CLICK_VERB: Record<ClickEvent['action'], string> = {
  open_url: 'Opens',
  run_command: 'Runs',
  suggest_command: 'Puts in the chat box:',
  copy_to_clipboard: 'Copies',
};

export const describeClick = (click: ClickEvent): string => `${CLICK_VERB[click.action]} ${click.value}`;

const OBFUSCATION_GLYPHS = 'ABCDEFGHJKLMNOPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz0123456789#$%&?@';

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

/** §k text: the same length, different characters every few frames (held still under reduced motion). */
const Obfuscated = ({ text }: { text: string }) => {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (prefersReducedMotion()) return;
    const id = window.setInterval(() => setTick((t) => t + 1), 90);
    return () => window.clearInterval(id);
  }, []);
  const scrambled = useMemo(
    () =>
      Array.from(text)
        .map((ch) => (/\s/.test(ch) ? ch : OBFUSCATION_GLYPHS[(ch.charCodeAt(0) * 31 + tick * 17 + Math.floor(Math.random() * 7)) % OBFUSCATION_GLYPHS.length]))
        .join(''),
    [text, tick],
  );
  return (
    <>
      <span aria-hidden="true">{scrambled}</span>
      <span className="sr-only">(obfuscated text)</span>
    </>
  );
};

function spanStyle(span: PreviewSpan, interactive: boolean): CSSProperties {
  const lines = [span.underlined && 'underline', span.strikethrough && 'line-through'].filter(Boolean).join(' ');
  const style: CSSProperties = {
    color: span.color,
    textShadow: `1px 1px 0 ${shadowColor(span.color)}`,
  };
  if (lines) {
    style.textDecorationLine = lines;
  } else if (interactive) {
    // Minecraft gives clickable text no marker of its own; a faint dotted rule
    // tells staff there is something to hover without pretending it is styling.
    style.textDecorationLine = 'underline';
    style.textDecorationStyle = 'dotted';
    style.textDecorationColor = 'rgba(255, 255, 255, 0.45)';
    style.textUnderlineOffset = '3px';
  }
  return style;
}

const SpanText = ({ span, compact }: { span: PreviewSpan; compact: boolean }) => {
  const text = compact ? span.text.replace(/\s*\n\s*/g, ' ') : span.text;
  return (
    <span
      className={cn(span.bold && 'font-bold', span.italic && 'italic', span.placeholder && 'opacity-80')}
      style={spanStyle(span, !compact && (!!span.hover || !!span.click))}
    >
      {span.obfuscated ? <Obfuscated text={text} /> : text}
    </span>
  );
};

/** The Minecraft tooltip: near-black with a purple rule, which is what players see on hover. */
const ChatTooltip = ({ anchor, children }: { anchor: HTMLElement; children: ReactNode }) => {
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const place = () => {
      const target = anchor.getBoundingClientRect();
      const box = ref.current?.getBoundingClientRect();
      const width = box?.width ?? 240;
      const height = box?.height ?? 40;
      const left = Math.min(Math.max(8, target.left), window.innerWidth - width - 8);
      const above = target.top - height - 8;
      setPosition({ left, top: above > 8 ? above : target.bottom + 8 });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [anchor]);

  return createPortal(
    <div
      ref={ref}
      role="tooltip"
      className="fixed z-[10000] max-w-xs rounded-sm px-2 py-1.5 text-[13px] font-medium leading-5 whitespace-pre-wrap break-words pointer-events-none"
      style={{
        left: position?.left ?? -9999,
        top: position?.top ?? -9999,
        background: 'rgba(16, 0, 16, 0.94)',
        border: '2px solid #2a0b52',
      }}
    >
      {children}
    </div>,
    document.body,
  );
};

const InteractiveSpan = ({ span }: { span: PreviewSpan }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const show = (event: { currentTarget: HTMLElement }) => setAnchor(event.currentTarget);
  const hide = () => setAnchor(null);
  const label = [span.hover ? 'Hover text' : null, span.click ? describeClick(span.click) : null].filter(Boolean).join('. ');

  return (
    <>
      <span
        tabIndex={0}
        aria-label={`${span.text}. ${label}`}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        className="cursor-help rounded-sm focus-visible:outline focus-visible:outline-1 focus-visible:outline-white/50"
      >
        <SpanText span={span} compact={false} />
      </span>
      {anchor && (
        <ChatTooltip anchor={anchor}>
          {span.hover && (
            <span className="block">
              {span.hover.map((inner, i) => (
                <SpanText key={i} span={inner} compact={false} />
              ))}
            </span>
          )}
          {span.click && (
            <span className={cn('block text-[11px] leading-4 text-white/60 font-normal', span.hover && 'mt-1 pt-1 border-t border-white/10')}>
              {describeClick(span.click)}
            </span>
          )}
        </ChatTooltip>
      )}
    </>
  );
};

export const TellrawPreview = memo(({ component, compact = false, emptyLabel = 'Nothing to show yet', className }: TellrawPreviewProps) => {
  const spans = useMemo(() => flattenComponent(component), [component]);
  const hasText = spans.some((s) => s.text.trim().length > 0);

  if (compact) {
    return (
      <span
        className={cn(
          'inline-block max-w-full truncate align-middle rounded-md bg-black/75 px-2 py-0.5 text-xs font-medium leading-5',
          className,
        )}
      >
        {hasText ? spans.map((span, i) => <SpanText key={i} span={span} compact />) : <span className="text-white/40">{emptyLabel}</span>}
      </span>
    );
  }

  return (
    <div
      className={cn(
        'rounded-lg bg-black/75 px-3 py-2.5 text-[13px] font-medium leading-5 whitespace-pre-wrap break-words',
        className,
      )}
    >
      {hasText ? (
        spans.map((span, i) =>
          span.hover || span.click ? <InteractiveSpan key={i} span={span} /> : <SpanText key={i} span={span} compact={false} />,
        )
      ) : (
        <span className="text-white/40">{emptyLabel}</span>
      )}
    </div>
  );
});

TellrawPreview.displayName = 'TellrawPreview';
